import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { ToastContainer } from '../components/UndoToast';
import './HomePage.css';

function HomePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeSessions, setActiveSessions] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [joinCode, setJoinCode] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    loadActiveSessions();
  }, []);

  const loadActiveSessions = async () => {
    try {
      const myCodes = JSON.parse(localStorage.getItem('my_sessions') || '[]');
      if (myCodes.length === 0) {
        setActiveSessions([]);
        return;
      }
      const sessions = await api.getSessionsBatch(myCodes);
      setActiveSessions(sessions);
    } catch (err) {
      console.error('Failed to load sessions:', err);
    }
  };

  const handleCreateSession = async () => {
    setLoading(true);
    setError('');
    
    try {
      const session = await api.createSession();
      // Remember this session code in localStorage
      const myCodes = JSON.parse(localStorage.getItem('my_sessions') || '[]');
      myCodes.push(session.code);
      localStorage.setItem('my_sessions', JSON.stringify(myCodes));
      navigate(`/admin/${session.code}`);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to create session. Please try again.';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSession = async (code) => {
    try {
      await api.closeSession(code);
      await loadActiveSessions();
      addToast(`Session ${code} closed`, async () => {
        await api.reopenSession(code);
        await loadActiveSessions();
      });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to close session.');
      setTimeout(() => setError(''), 3000);
    }
  };

  const addToast = useCallback((message, undoAction) => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, undoAction }]);
    return id;
  }, []);

  const handleToastUndo = useCallback(async (toastId) => {
    const toast = toasts.find(t => t.id === toastId);
    setToasts(prev => prev.filter(t => t.id !== toastId));
    if (toast?.undoAction) {
      await toast.undoAction();
    }
  }, [toasts]);

  const handleToastExpire = useCallback((toastId) => {
    setToasts(prev => prev.filter(t => t.id !== toastId));
  }, []);

  const handleRemoveSession = (code) => {
    const myCodes = JSON.parse(localStorage.getItem('my_sessions') || '[]');
    const updated = myCodes.filter(c => c !== code);
    localStorage.setItem('my_sessions', JSON.stringify(updated));
    setActiveSessions(prev => prev.filter(s => s.code !== code));
  };

  const handleJoin = (e) => {
    e.preventDefault();
    const code = joinCode.trim();
    if (!code) return;
    navigate(`/vote/${code}`);
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="home-page">
      <div className="home-container">
        <h1>Confidence Vote</h1>
        <p className="subtitle">for PI Planning</p>
        
        <button 
          className="create-session-btn"
          onClick={handleCreateSession}
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Start New Session'}
        </button>
        
        {error && <p className="error-message">{error}</p>}

        <div className="join-divider"><span>or join a vote</span></div>

        <form className="join-form" onSubmit={handleJoin}>
          <input
            className="join-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={5}
            placeholder="Enter code"
            value={joinCode}
            onChange={(e) => setJoinCode(e.target.value.replace(/\D/g, ''))}
            aria-label="Session code"
          />
          <button type="submit" className="join-btn" disabled={!joinCode.trim()}>
            Join
          </button>
        </form>
      </div>

      {activeSessions.length > 0 && (
        <div className="active-sessions">
          <h2>My Sessions</h2>
          <div className="session-list">
            {activeSessions.map((session) => (
              <div key={session.code} className="session-card">
                <div className="session-code">{session.code}</div>
                <div className="session-info">
                  <span className="session-votes">
                    {session.vote_count} / {session.max_voters} votes
                  </span>
                  <span className="session-time">Started {formatTime(session.created_at)}</span>
                  {session.anonymous && <span className="session-badge">🔒 Anonymous</span>}
                  {session.closed && <span className="session-badge closed-badge">⛔ Closed</span>}
                </div>
                <div className="session-actions">
                  <button
                    className="session-btn admin-btn"
                    onClick={() => navigate(`/admin/${session.code}`)}
                  >
                    Admin
                  </button>
                  {!session.closed && (
                    <button
                      className="session-btn close-btn"
                      onClick={() => handleCloseSession(session.code)}
                    >
                      Close
                    </button>
                  )}
                  <button
                    className="session-btn remove-btn"
                    onClick={() => handleRemoveSession(session.code)}
                    title="Remove from My Sessions"
                  >
                    ✕
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <ToastContainer
        toasts={toasts}
        onUndo={handleToastUndo}
        onExpire={handleToastExpire}
      />
    </div>
  );
}

export default HomePage;
